import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  ComponentType,
} from 'react';
import styled from 'styled-components';
import { useInView } from 'react-hook-inview';
import { useMedia } from 'react-use';
import { StickyMenuContext } from 'src/components/common/StickyMenu/StickyMenuContext';
import { StyleProps } from '@summer/jst-react';

export type InViewThreshold = number | number[];

export interface StickyMenuSectionProps {
  as?: string | ComponentType<any>;
  id: string | null;
  label?: string;
  index?: number;
  threshold:
    | InViewThreshold
    | {
        desktop: InViewThreshold;
        mobile: InViewThreshold;
      };
}

const Section = styled.section``;

export const StickyMenuSection: FC<StickyMenuSectionProps & StyleProps> = ({
  id,
  label,
  index,
  threshold,
  children,
  ...props
}) => {
  const isDesktop = useMedia('(min-width: 992px)', true);

  const activeThreshold = useMemo(() => {
    if (typeof threshold === 'object' && !Array.isArray(threshold)) {
      if (isDesktop) return threshold.desktop;
      return threshold.mobile;
    }
    return threshold;
  }, [threshold, isDesktop]);

  const [ref, isVisible] = useInView({
    threshold: activeThreshold,
  });

  const { handleActiveSectionChange, registerSection } =
    useContext(StickyMenuContext);

  useEffect(() => {
    if (isVisible) {
      handleActiveSectionChange(id);
    }
  }, [isVisible, handleActiveSectionChange, id]);

  const hasChildren = !!children;

  useEffect(() => {
    if (id && label && hasChildren) {
      registerSection({ id, label, index });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, label, hasChildren]);

  return (
    <Section {...props} ref={ref} id={id ?? undefined}>
      {children}
    </Section>
  );
};
